import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { withRights } from 'providers/RightsConsumer';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { TP_DATE, TP_NUM } from 'constants/inputTypes';

const OfferAssetSecondHandComponent = React.memo(
  ({
    t,
    onPropertyChange,
    rights,
    buildYearAndMonthError,
    isSecondHand,
    buildYearAndMonth,
    secondHandProperty,
    maxMonths,
    disabled,
    processing,
    addToRefs
  }) => {
    const isValidDate = current => {
      if (current) {
        const calendarMaximumMonths = moment().subtract(maxMonths, 'months');
        const currentMonth = moment();
        return current.isBefore(currentMonth) && current.isAfter(calendarMaximumMonths);
      } else {
        return true;
      }
    };

    return (
      <Grid container spacing={3} alignItems='center'>
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={
              <Switch
                id={'offerAssetCard_secondHand_isSecondHand'}
                checked={isSecondHand || false}
                onChange={onCheckBoxChange(onPropertyChange('isSecondHand'))}
                disabled={rights.readOnlyFull || !rights.financedAssetWrite || disabled?.isSecondHand || processing}
              />
            }
            label={t('OfferEdit.Asset.Info.SecondHand')}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              id={t('OfferEdit.Asset.Info.BuildYearAndMonth')}
              ref={ref => addToRefs(ref, t('OfferEdit.Asset.Info.BuildYearAndMonth'), TP_DATE)}
            >
              <DateTime
                id={'offerAssetCard_secondHand_buildYearAndMonth'}
                value={buildYearAndMonth}
                label={t('OfferEdit.Asset.Info.BuildYearAndMonth')}
                onChange={onPropertyChange('buildYearAndMonth')}
                error={Boolean(buildYearAndMonthError)}
                helperText={buildYearAndMonthError}
                inputFormat='yyyy-MM'
                closeOnSelect={true}
                disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing}
                isValidDate={isValidDate}
                required
              />
            </Grid>
            {secondHandProperty.visible && (
              <Grid item xs={12} sm={6} md={6} id={secondHandProperty.label} ref={ref => addToRefs(ref, secondHandProperty.label, TP_NUM)}>
                <TextField
                  id={'offerAssetCard_secondHand_property'}
                  required
                  fullWidth
                  isNumeric
                  debounceBy={500}
                  error={secondHandProperty.mandatory}
                  helperText={secondHandProperty.error}
                  value={secondHandProperty.value || ''}
                  onChange={secondHandProperty.onChange}
                  label={secondHandProperty.label}
                  inputProps={{ decimalScale: 0 }}
                  disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing || secondHandProperty.disabled}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

OfferAssetSecondHandComponent.displayName = 'OfferAssetSecondHandComponent';

OfferAssetSecondHandComponent.propTypes = {
  t: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  isSecondHand: PropTypes.bool,
  buildYearAndMonth: PropTypes.any,
  buildYearAndMonthError: PropTypes.string,
  secondHandProperty: PropTypes.object,
  disabled: PropTypes.object,
  processing: PropTypes.bool,
  maxMonths: PropTypes.number,
  addToRefs: PropTypes.func.isRequired
};

const MemoizedOfferAssetSecondHandComponent = withTranslation()(withRights(OfferAssetSecondHandComponent));

const ForwardedOfferAssetSecondHandComponent = forwardRef((props, ref) => (
  <MemoizedOfferAssetSecondHandComponent {...props} forwardedRef={ref} />
));

ForwardedOfferAssetSecondHandComponent.displayName = 'ForwardedOfferAssetSecondHandComponent';

export default ForwardedOfferAssetSecondHandComponent;
